import React, { useEffect, useState } from "react";
import {
  Form, Input, Button, Select, DatePicker,
  TimePicker, Spin, Row, Col, message, Drawer
} from "antd";
import { PlusOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { createConference, fetchConferenceById, updateConference } from "../../features/conferenceSlice";
import { fetchLocations } from "../../features/locationSlice";
import { fetchUsers } from "../../features/userSlice";
import { useNavigate, useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import dayjs from "dayjs";
import App from "../../Utils/LayoutMobile";
import TimeSlotField from "./TimeSlotField";

// ── Theme ──
const C_PINK      = "#F04DA0";
const C_PURPLE    = "#8B2FC9";
const C_GRAD      = `linear-gradient(135deg, ${C_PINK}, ${C_PURPLE})`;
const C_SOFT_BG   = "#F8F4FF";
const C_TEXT_MAIN = "#1a0040";
const C_TEXT_MUTED= "#64748B";

const previewTheme = {
  purple:     '#6b19a3',
  purpleDark: '#4a1171',
  fuchsia:    '#c026d3',
  bgLight:    '#f6effa',
  bgTimer:    '#eed3f1',
};

// ── Icons ──
const SaveIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/>
    <polyline points="7 3 7 8 15 8"/>
  </svg>
);
const CancelIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const EventIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const DragIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="8" y1="6" x2="16" y2="6"/>
    <line x1="8" y1="12" x2="16" y2="12"/>
    <line x1="8" y1="18" x2="16" y2="18"/>
  </svg>
);

function SectionLabel({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:C_PINK, whiteSpace:"nowrap" }}>
        {children}
      </span>
      <span style={{ flex:1, height:1.5, background:`linear-gradient(90deg, rgba(240,77,160,0.35), transparent)`, borderRadius:2 }}/>
    </div>
  );
}

const conferenceTypeEnum  = ["conference", "workshop"];
const ConferenceStatusEnum = ["upcoming", "ongoing", "completed"];

// ── Smart Dropdown Logic ──
const getDropdownType = (title) => {
  const t = title?.toUpperCase().trim() || '';
  const noDropdown = [
    'REGISTRATION','BREAKFAST','LUNCH','BREAK','TEA BREAK',
    'DINNER','SNACKS','NETWORKING','REFRESHMENT'
  ];
  if (noDropdown.some(k => t.includes(k))) return 'none';
  return 'doctor';
};

// ── Helper Functions ──
const formatDate = (dateStr) => {
  if (!dateStr) return { day: '--', month: '---', year: '----' };
  const d = new Date(dateStr);
  return { day: d.getDate(), month: d.toLocaleString('default', { month: 'short' }), year: d.getFullYear() };
};

const getImagePath = (rawPath) => {
  if (!rawPath) return "/Image/default-doctor.jpg";
  return rawPath.replace(/^"|"$/g, "").replace(/^C:\\.*?public/, "");
};

const convertTo12Hour = (time) => {
  if (!time) return "";
  if (/am|pm/i.test(time)) return time;
  const [h, m] = time.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const convertRange = (range) => {
  if (!range) return "";
  if (!range.includes('-')) return convertTo12Hour(range);
  const [s, e] = range.split('-').map(t => t.trim());
  return `${convertTo12Hour(s)} – ${convertTo12Hour(e)}`;
};

// ════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════
const ConferenceForm = () => {
  const [form]     = Form.useForm();
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { id }     = useParams();             // id असेल तर Update, नसेल तर Create
  const isEdit     = Boolean(id);

  const { current, loading: confLoading } = useSelector((s) => s.conference);
  const { locations, loading: locLoading } = useSelector((s) => s.locations);
  const { users,     loading: userLoading } = useSelector((s) => s.user);
  const [conferenceType, setConferenceType] = useState("");
  const [submitting,     setSubmitting]     = useState(false);
  const [sessionTitles,  setSessionTitles]  = useState([]); // Smart dropdown tracking
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData,    setPreviewData]    = useState([]);


  // Users role-wise split
  const doctors       = users?.filter(u => u.role === 'doctor'       || u.role === 'faculty') || [];
  const receptionists = users?.filter(u => u.role === 'Receptionist') || [];

  // ── Fetch initial data ──
  useEffect(() => {
    const load = async () => {
      try {
        await Promise.all([
          dispatch(fetchLocations()).unwrap(),
          dispatch(fetchUsers({ status: "false", role: "faculty" })).unwrap(),
        ]);
      } catch (e) {
        message.error("Failed to load data");
      }
    };
    load();
  }, [dispatch]);

  // ── If Edit: fetch conference ──
  useEffect(() => {
    if (isEdit) dispatch(fetchConferenceById(id));
  }, [dispatch, id, isEdit]);

  // ── If Edit: populate form ──
  useEffect(() => {
    if (isEdit && current && current._id === id) {
      form.setFieldsValue({
        title:          current.title,
        conferenceType: current.conferenceType,
        date:           current.date       ? dayjs(current.date)              : null,
        startTime:      current.startTime  ? dayjs(current.startTime, "HH:mm"): null,
        endTime:        current.endTime    ? dayjs(current.endTime,   "HH:mm"): null,
        location:       current.location,
        status:         current.status,
        doctor:         current.doctor || undefined,
        description:    current.description,
        price_and_time: current.price_and_time?.map(p => ({
          ...p, date: p.date ? dayjs(p.date) : null,
        })),
        info: current.info?.map(item => ({
          ...item,
          timeSlots:      item.timeSlots      || [],
          categoryPrices: item.categoryPrices || [],
        })),
      });
      setConferenceType(current.conferenceType);
      // Session titles track करतो
      setSessionTitles(current.info?.map(i => i.title || '') || []);
    }
  }, [current?._id, form, id, isEdit]);

  // ── Session title change handler (smart dropdown) ──
  const handleSessionTitleChange = (index, value) => {
    setSessionTitles(prev => {
      const updated = [...prev];
      updated[index] = value || '';
      return updated;
    });
  };

  // ── Drag & Drop ──
  const handleDragEnd = (result, fields, move) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    move(result.source.index, result.destination.index);
    // sessionTitles reorder
    setSessionTitles(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(result.source.index, 1);
      arr.splice(result.destination.index, 0, moved);
      return arr;
    });
  };

  // ── Submit ──
  const onFinish = (values) => {
    setSubmitting(true);
    try {
      const payload = {
        ...values,
        title:     values.title?.trim(),
        date:      values.date      ? dayjs(values.date).format("YYYY-MM-DD") : null,
        startTime: values.startTime ? dayjs(values.startTime).format("HH:mm") : null,
        endTime:   values.endTime   ? dayjs(values.endTime).format("HH:mm")   : null,
        price_and_time: values.price_and_time?.map(p => ({
          ...p, date: p.date ? dayjs(p.date).format("YYYY-MM-DD") : null,
        })),
        info: values.info?.map(item => ({
          ...item,
          timeSlots:      item.timeSlots      || [],
          categoryPrices: item.categoryPrices || [],
        })),
      };

      const action = isEdit
        ? dispatch(updateConference({ id, data: payload }))
        : dispatch(createConference(payload));

      action
        .unwrap()
        .then(() => {
          message.success(isEdit ? "Event updated!" : "Event created!");
          form.resetFields();
          navigate("/event");
        })
        .catch(err => message.error("Failed: " + (err?.message || '')))
        .finally(() => setSubmitting(false));

    } catch (e) {
      message.error("Data formatting error");
      setSubmitting(false);
    }
  };

  // ── Preview ──
  const handlePreview = () => {
    const vals = form.getFieldsValue(true);
    const mappedInfo = (vals.info || []).map(session => {
      const fullDoctor = users?.find(u => u._id === session.doctor);
      return { ...session, doctor: fullDoctor || null };
    });
    setPreviewData([{ ...vals, info: mappedInfo }]);
    setPreviewVisible(true);
  };

  const isLoading = (isEdit ? confLoading : false) || locLoading || userLoading;

  return (
    <App>
      <div className="lc">
        <div className="mw">

          {/* Header */}
          <div className="header-flex">
            <div>
              <SectionLabel>Event Setup</SectionLabel>
              <h1 className="sec-title">
                {isEdit ? "Update" : "Create"}{" "}
                <span style={{ color: C_PINK }}>Event</span>
              </h1>
              <p className="sec-sub">
                {isEdit
                  ? "Modify schedule, sessions, locations, and ticketing information."
                  : "Define the schedule, sessions, locations, and ticketing for a new event."}
              </p>
            </div>
            <div className="icon-wrap"><EventIcon /></div>
          </div>

          <Spin spinning={submitting || isLoading}
            tip={submitting ? (isEdit ? "Updating..." : "Creating...") : "Loading..."}>
            <div className="glass">
              <Form
                layout="vertical"
                form={form}
                onFinish={onFinish}
                className="premium-form"
                requiredMark={false}
              >

                {/* 1. Basic Details */}
                <h3 className="form-section-title">Basic Details</h3>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item name="title" label="Event Title" rules={[{ required: true }]}>
                      <Input placeholder="e.g. Skinola Annual Conference" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="conferenceType" label="Event Type" rules={[{ required: true }]}>
                      <Select placeholder="Select type" onChange={setConferenceType} className="custom-select">
                        {conferenceTypeEnum.map(t => (
                          <Select.Option key={t} value={t}>{t}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="status" label="Status"
                      initialValue={isEdit ? undefined : "upcoming"}
                      rules={[{ required: true }]}>
                      <Select className="custom-select">
                        {ConferenceStatusEnum.map(s => (
                          <Select.Option key={s} value={s}>{s}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  {conferenceType === "conference" && (
                    <Col xs={24} md={12}>
                      <Form.Item name="doctor" label="Assign Doctor" rules={[{ required: true }]}>
                        <Select placeholder="Select doctor" showSearch optionFilterProp="children" className="custom-select">
                          {doctors.map(u => (
                            <Select.Option key={u._id} value={u._id}>{u.name}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  )}
                  <Col xs={24} md={12}>
                    <Form.Item name="location" label="Venue Location" rules={[{ required: true }]}>
                      <Select placeholder="Select location" className="custom-select" value={form.getFieldValue("location")}>
                        {locations?.filter(l => l.type == "herolocation").map(l => (
                          <Select.Option key={l._id} value={l._id}>{l.title}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <div className="form-divider" />

                {/* 2. Primary Schedule */}
                <h3 className="form-section-title">Primary Schedule</h3>
                <Row gutter={24}>
                  <Col xs={24} md={8}>
                    <Form.Item name="date" label="Event Date" rules={[{ required: true }]}>
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="startTime" label="Start Time" rules={[{ required: true }]}>
                      <TimePicker format="h:mm A" use12Hours style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="endTime" label="End Time" rules={[{ required: true }]}>
                      <TimePicker format="h:mm A" use12Hours style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="form-divider" />

                {/* 3. Sessions — Drag & Drop + Smart Dropdown */}
                <h3 className="form-section-title">Event Sessions</h3>
                <Form.List name="info">
                  {(fields, { add, remove, move }) => (
                    <>
                      <DragDropContext onDragEnd={(r) => handleDragEnd(r, fields, move)}>
                        <Droppable droppableId="sessions-droppable">
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {fields.map(({ key, name, ...restField }, index) => {
                                const currentTitle = sessionTitles[index] || '';
                                const dropType    = getDropdownType(currentTitle);

                                return (
                                  <Draggable
                                    key={`session-drag-${key}`}
                                    draggableId={`session-drag-${key}`}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className="nested-card"
                                        style={{
                                          ...provided.draggableProps.style,
                                          background: snapshot.isDragging ? '#fdf4ff' : '#faf8fc',
                                          border: snapshot.isDragging
                                            ? `2px dashed ${C_PINK}`
                                            : '1px solid rgba(139,47,201,0.12)',
                                          boxShadow: snapshot.isDragging
                                            ? '0 8px 24px rgba(240,77,160,0.2)'
                                            : undefined,
                                        }}
                                      >
                                        {/* Card Header */}
                                        <div className="nested-card-header">
                                          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                            {/* Drag Handle */}
                                            <div
                                              {...provided.dragHandleProps}
                                              style={{ color:'#c4b5fd', cursor:'grab', display:'flex', alignItems:'center' }}
                                              title="Drag to reorder"
                                            >
                                              <DragIcon />
                                            </div>
                                            <h4>Session {index + 1}</h4>
                                          </div>
                                          <Button type="text" danger icon={<DeleteOutlined />}
                                            onClick={() => {
                                              remove(name);
                                              setSessionTitles(prev => prev.filter((_, i) => i !== index));
                                            }}>
                                            Remove
                                          </Button>
                                        </div>

                                        {/* Title + Smart Dropdown Row */}
                                        <Row gutter={16}>
                                          <Col xs={24} md={12}>
                                            <Form.Item
                                              {...restField}
                                              name={[name, "title"]}
                                              label="Session Title"
                                              rules={[{ required: true }]}
                                            >
                                              <Input
                                                placeholder="e.g. REGISTRATION, BREAKFAST, HAIR TRANSPLANT..."
                                                onChange={(e) => handleSessionTitleChange(index, e.target.value)}
                                              />
                                            </Form.Item>
                                          </Col>

                                          {/* ── Smart Dropdown ── */}
                                          {dropType === 'doctor' && (
                                            <Col xs={24} md={12}>
                                              <Form.Item
                                                {...restField}
                                                name={[name, "doctor"]}
                                                label="Session Doctor"
                                                rules={[{ required: true }]}
                                              >
                                                <Select
                                                  placeholder="Select Doctor"
                                                  showSearch
                                                  optionFilterProp="children"
                                                  className="custom-select"
                                                >
                                                  {doctors.map(u => (
                                                    <Select.Option key={u._id} value={u._id}>
                                                      {u.name}
                                                    </Select.Option>
                                                  ))}
                                                </Select>
                                              </Form.Item>
                                            </Col>
                                          )}

                                          {dropType === 'receptionist' && (
                                            <Col xs={24} md={12}>
                                              <Form.Item
                                                {...restField}
                                                name={[name, "doctor"]}
                                                label="Receptionist"
                                                rules={[{ required: true }]}
                                              >
                                                <Select
                                                  placeholder="Select Receptionist"
                                                  showSearch
                                                  optionFilterProp="children"
                                                  className="custom-select"
                                                >
                                                  {receptionists.map(u => (
                                                    <Select.Option key={u._id} value={u._id}>
                                                      {u.name}
                                                    </Select.Option>
                                                  ))}
                                                </Select>
                                              </Form.Item>
                                            </Col>
                                          )}

                                          {/* dropType === 'none' → dropdown नाही */}
                                        </Row>

                                        {/* Time Slot */}
                                        <Row gutter={16}>
                                          <Col xs={24} md={12}>
                                            <TimeSlotField
                                              form={form}
                                              name={["info", name, "timeSlots"]}
                                              initialTimeSlot={isEdit ? current?.info?.[name]?.timeSlots?.[0] : undefined}
                                            />
                                          </Col>
                                        </Row>

                                        {/* Category Prices */}
                                        <div className="inner-nested-list">
                                          <h5>Session Pricing Categories</h5>
                                          <Form.List name={[name, "categoryPrices"]}>
                                            {(catFields, { add: addCat, remove: removeCat }) => (
                                              <>
                                                {catFields.map(cat => (
                                                  <Row gutter={12} key={cat.key} align="middle" style={{ marginBottom:12 }}>
                                                    <Col span={10}>
                                                      <Form.Item {...cat} name={[cat.name, "category"]} noStyle rules={[{ required: true }]}>
                                                        <Select placeholder="Category" className="custom-select">
                                                          <Select.Option value="Early bird">Early bird</Select.Option>
                                                          <Select.Option value="Timely">Timely</Select.Option>
                                                          <Select.Option value="Spot">Spot</Select.Option>
                                                        </Select>
                                                      </Form.Item>
                                                    </Col>
                                                    <Col span={10}>
                                                      <Form.Item {...cat} name={[cat.name, "price"]} noStyle rules={[{ required: true }]}>
                                                        <Input type="number" placeholder="Price (e.g. 500)" />
                                                      </Form.Item>
                                                    </Col>
                                                    <Col span={4}>
                                                      <Button danger type="text" icon={<DeleteOutlined />} onClick={() => removeCat(cat.name)} />
                                                    </Col>
                                                  </Row>
                                                ))}
                                                <Button type="dashed" onClick={() => addCat()} block className="add-btn-small" icon={<PlusOutlined />}>
                                                  Add Price Category
                                                </Button>
                                              </>
                                            )}
                                          </Form.List>
                                        </div>

                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>

                      <Button type="dashed" onClick={() => {
                        add();
                        setSessionTitles(prev => [...prev, '']);
                      }} block className="add-btn" icon={<PlusOutlined />}>
                        Add New Session
                      </Button>
                    </>
                  )}
                </Form.List>

                <div className="form-divider" />

                {/* 4. Global Ticket Prices */}
                <h3 className="form-section-title">Global Event Tickets</h3>
                <Form.List name="price_and_time">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <div key={key} className="nested-card">
                          <div className="nested-card-header">
                            <h4>Ticket Entry {name + 1}</h4>
                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)}>Remove</Button>
                          </div>
                          <Row gutter={16}>
                            <Col xs={24} md={8}>
                              <Form.Item {...restField} name={[name, "title"]} label="Ticket Type" rules={[{ required: true }]}>
                                <Select placeholder="Category" className="custom-select">
                                  <Select.Option value="Early bird">Early bird</Select.Option>
                                  <Select.Option value="Timely">Timely</Select.Option>
                                  <Select.Option value="Spot">Spot</Select.Option>
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                              <Form.Item {...restField} name={[name, "price"]} label="Price" rules={[{ required: true }]}>
                                <Input type="number" placeholder="e.g., 499" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                              <Form.Item {...restField} name={[name, "date"]} label="Valid Until" rules={[{ required: true }]}>
                                <DatePicker style={{ width: "100%" }} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      ))}
                      <Button type="dashed" onClick={() => add()} block className="add-btn" icon={<PlusOutlined />}>
                        Add Global Ticket Type
                      </Button>
                    </>
                  )}
                </Form.List>

                {/* Actions */}
                <div className="form-actions">
                  <button type="button" className="btn-cancel"
                    onClick={() => navigate("/event")} disabled={submitting}>
                    <CancelIcon /> Cancel
                  </button>
                  <button type="submit" className="btn-cta" disabled={submitting}>
                    <SaveIcon /> {isEdit ? "Update Event" : "Create Event"}
                  </button>
                </div>

              </Form>
            </div>
          </Spin>
        </div>
      </div>

      {/* Floating Preview Button */}
      <button className="floating-preview-btn" onClick={handlePreview} title="Live Preview">
        <EyeOutlined style={{ fontSize:'20px' }} />
        <span>Live Preview</span>
      </button>

      {/* Preview Drawer */}
      <Drawer
        title={<span style={{ fontWeight:800, color:C_TEXT_MAIN }}>Website Live Preview</span>}
        placement="right"
        width="100%"
        onClose={() => setPreviewVisible(false)}
        open={previewVisible}
        bodyStyle={{ padding:0, background:previewTheme.bgLight }}
      >
        <div className="website-preview-root">
          <div className="cs-page">
            <div className="cs-header">
              <div className="cs-eyebrow">
                <span className="cs-eyebrow-dot" />
                Dermatology & Aesthetics
              </div>
              <h1 className="cs-main-title">{previewData[0]?.title || "Event Title"}</h1>
              <h2 className="cs-sub-title">Scientific Schedule Plan</h2>
              <div className="cs-title-line" />
            </div>

            <div className="cs-list">
              {!previewData[0]?.info?.length ? (
                <div className="cs-empty"><p>No sessions added yet.</p></div>
              ) : (
                previewData.map((schedule, idx) => {
                  const { day, month, year } = formatDate(schedule.date);
                  return (
                    <div key={idx} className="cs-block">
                      <div className="cs-date-bar">
                        <div className="cs-date-left">
                          <span className="cs-day">{day}</span>
                          <span className="cs-month-year">{month}<br />{year}</span>
                        </div>
                        <div className="cs-date-right">
                          <div className="cs-badge">{schedule.conferenceType || "Conference"}</div>
                          <p className="cs-program-label">Conference Program and Scientific Sessions</p>
                        </div>
                      </div>

                      <div className="cs-sessions">
                        <div className="cs-table-head">
                          <span className="cs-th">Time</span>
                          <span className="cs-th">Session</span>
                          <span className="cs-th">Speaker</span>
                        </div>
                        <div className="cs-sessions-inner">
                          {schedule.info.map((session, i) => (
                            <div key={i} className="cs-session-row">
                              <div className="cs-session-time">
                                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ flexShrink:0 }}>
                                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                </svg>
                                {session.timeSlots?.map(convertRange).join(" / ") || "TBA"}
                              </div>
                              <div className="cs-session-title">{session.title || "Untitled"}</div>
                              {session.doctor ? (
                                <div className="cs-session-doctor">
                                  <div className="cs-doctor-avatar-wrap">
                                    <img
                                      src={getImagePath(session.doctor.image)}
                                      alt={session.doctor.name}
                                      className="cs-doctor-avatar"
                                      onError={e => { e.target.onerror=null; e.target.src="/Image/default-doctor.jpg"; }}
                                    />
                                  </div>
                                  <div className="cs-doctor-info">
                                    <span className="cs-doctor-role">Speaker</span>
                                    <span className="cs-doctor-name">{session.doctor.name}</span>
                                  </div>
                                </div>
                              ) : <div className="cs-doctor-placeholder" />}
                            </div>
                          ))}
                        </div>
                        <div className="cs-block-footer">
                          <button className="cs-register-btn">Register Now</button>
                          <button className="cs-download-btn">Download Schedule</button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </Drawer>

      {/* ── All Styles ── */}
      <style>{`
        * { box-sizing: border-box; }
        .lc { font-family: 'Segoe UI', sans-serif; background: ${C_SOFT_BG}; min-height: 100vh; padding: 40px 0 80px; }
        .mw { max-width: 900px; margin: 0 auto; padding: 0 24px; }
        .header-flex { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 32px; }
        .sec-title { font-size: clamp(28px,4vw,38px); font-weight: 800; color: ${C_TEXT_MAIN}; letter-spacing: -0.02em; line-height: 1.15; margin: 0 0 8px; }
        .sec-sub { font-size: 15px; color: ${C_TEXT_MUTED}; margin: 0; }
        .icon-wrap { width: 54px; height: 54px; background: rgba(240,77,160,0.1); border: 1px solid rgba(240,77,160,0.2); border-radius: 16px; display: flex; align-items: center; justify-content: center; color: ${C_PINK}; }
        .glass { background: #fff; border-radius: 20px; padding: 36px 40px; border: 1px solid rgba(139,47,201,0.08); box-shadow: 0 10px 40px rgba(0,0,0,0.04); }
        .form-section-title { font-size: 17px; font-weight: 800; color: ${C_PURPLE}; margin-top: 0; margin-bottom: 20px; }
        .form-divider { height: 1px; background: rgba(139,47,201,0.08); margin: 32px 0; }
        .premium-form .ant-form-item-label > label { font-weight: 600 !important; color: ${C_TEXT_MAIN} !important; font-size: 13.5px !important; }
        .premium-form .ant-input, .premium-form .ant-picker, .custom-select .ant-select-selector { padding: 10px 16px !important; border-radius: 10px !important; border: 1px solid rgba(139,47,201,0.15) !important; background: #faf6fe !important; color: ${C_TEXT_MAIN} !important; font-size: 14.5px !important; transition: all 0.3s; min-height: 44px; }
        .premium-form .ant-input:focus, .premium-form .ant-picker-focused, .custom-select:hover .ant-select-selector { border-color: ${C_PINK} !important; background: #fff !important; box-shadow: 0 0 0 3px rgba(240,77,160,0.1) !important; }
        .nested-card { background: #faf8fc; border: 1px solid rgba(139,47,201,0.12); border-radius: 14px; padding: 20px; margin-bottom: 20px; }
        .nested-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; border-bottom: 1px solid rgba(139,47,201,0.08); padding-bottom: 12px; }
        .nested-card-header h4 { margin: 0; font-size: 15px; font-weight: 700; color: ${C_TEXT_MAIN}; }
        .inner-nested-list { background: #fff; border-radius: 10px; padding: 16px; border: 1px dashed rgba(139,47,201,0.2); margin-top: 12px; }
        .inner-nested-list h5 { margin: 0 0 12px 0; font-size: 13px; font-weight: 600; color: ${C_TEXT_MUTED}; text-transform: uppercase; letter-spacing: 0.05em; }
        .add-btn { height: 44px; border-radius: 10px; border-color: rgba(139,47,201,0.3); color: ${C_PURPLE}; font-weight: 600; background: rgba(139,47,201,0.03); margin-top: 8px; }
        .add-btn:hover { border-color: ${C_PURPLE}; color: ${C_PURPLE}; background: rgba(139,47,201,0.08); }
        .add-btn-small { height: 38px; border-radius: 8px; font-size: 13px; color: ${C_PINK}; border-color: rgba(240,77,160,0.3); }
        .form-actions { display: flex; align-items: center; justify-content: flex-end; gap: 16px; margin-top: 40px; padding-top: 24px; border-top: 1px solid rgba(139,47,201,0.08); }
        .btn-cta { display: inline-flex; align-items: center; gap: 8px; padding: 14px 28px; background: ${C_GRAD}; border: none; border-radius: 50px; color: #fff; font-size: 14.5px; font-weight: 700; cursor: pointer; box-shadow: 0 10px 24px rgba(240,77,160,0.3); transition: all 0.25s; }
        .btn-cta:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 28px rgba(240,77,160,0.4); }
        .btn-cta:disabled { opacity: 0.7; cursor: not-allowed; }
        .btn-cancel { display: inline-flex; align-items: center; gap: 8px; padding: 14px 24px; background: transparent; border: 1px solid rgba(139,47,201,0.2); border-radius: 50px; color: ${C_TEXT_MUTED}; font-size: 14.5px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
        .btn-cancel:hover:not(:disabled) { background: #faf6fe; color: ${C_TEXT_MAIN}; border-color: rgba(139,47,201,0.4); }
        .floating-preview-btn { position: fixed; bottom: 30px; right: 30px; z-index: 1000; background: linear-gradient(135deg, #c026d3, #6b19a3); color: white; border: none; border-radius: 50px; padding: 14px 24px; box-shadow: 0 6px 20px rgba(192,38,211,0.4); cursor: pointer; display: flex; align-items: center; gap: 10px; font-weight: 700; font-size: 15px; transition: transform 0.2s, box-shadow 0.2s; }
        .floating-preview-btn:hover { transform: translateY(-3px); box-shadow: 0 10px 24px rgba(192,38,211,0.5); }
        @media(max-width: 768px) {
          .glass { padding: 24px 20px; border-radius: 16px; }
          .header-flex { flex-direction: column; align-items: flex-start; gap: 16px; }
          .icon-wrap { display: none; }
          .form-actions { flex-direction: column-reverse; align-items: stretch; }
          .btn-cta, .btn-cancel { width: 100%; justify-content: center; margin-bottom: 8px; }
          .floating-preview-btn { bottom: 20px; right: 20px; padding: 12px 20px; }
        }
        @keyframes pulseDot { 0%,100%{box-shadow:0 0 0 0 rgba(192,38,211,0.5)} 50%{box-shadow:0 0 0 6px rgba(192,38,211,0)} }
        .website-preview-root { font-family: system-ui,-apple-system,sans-serif; }
        .website-preview-root .cs-page { width:100%; background:${previewTheme.bgLight}; padding:24px 0 64px; }
        .website-preview-root .cs-header,.website-preview-root .cs-list { width:90vw; max-width:1350px; margin-left:auto; margin-right:auto; }
        .website-preview-root .cs-header { text-align:center; display:flex; flex-direction:column; align-items:center; padding-top:16px; margin-bottom:36px; }
        .website-preview-root .cs-eyebrow { display:inline-flex; align-items:center; gap:8px; background:${previewTheme.bgLight}; border:1.5px solid rgba(107,25,163,0.15); color:${previewTheme.purple}; font-size:12px; font-weight:700; letter-spacing:0.12em; text-transform:uppercase; padding:6px 16px 6px 12px; border-radius:50px; margin-bottom:16px; }
        .website-preview-root .cs-eyebrow-dot { width:7px; height:7px; border-radius:50%; background:${previewTheme.fuchsia}; animation:pulseDot 2s infinite; }
        .website-preview-root .cs-main-title { font-size:clamp(22px,4vw,42px); font-weight:800; color:${previewTheme.purpleDark}; margin:0 0 6px; line-height:1.2; }
        .website-preview-root .cs-sub-title { font-size:clamp(13px,2vw,17px); font-weight:500; color:#7a6a8a; margin:0 0 14px; }
        .website-preview-root .cs-title-line { width:56px; height:4px; border-radius:4px; background:linear-gradient(90deg,${previewTheme.fuchsia},${previewTheme.purple}); }
        .website-preview-root .cs-list { display:flex; flex-direction:column; gap:28px; }
        .website-preview-root .cs-empty { padding:56px 20px; text-align:center; color:${previewTheme.purple}; font-weight:500; background:${previewTheme.bgLight}; border-radius:20px; border:1px dashed rgba(107,25,163,0.2); }
        .website-preview-root .cs-block { border-radius:20px; overflow:hidden; border:1px solid rgba(107,25,163,0.1); box-shadow:0 8px 28px rgba(107,25,163,0.07); background:#fff; }
        .website-preview-root .cs-date-bar { display:flex; align-items:center; gap:24px; background:linear-gradient(135deg,${previewTheme.purpleDark},${previewTheme.purple}); padding:18px 28px; }
        .website-preview-root .cs-date-left { display:flex; align-items:center; gap:12px; flex-shrink:0; }
        .website-preview-root .cs-day { font-size:52px; font-weight:800; color:#fff; line-height:1; letter-spacing:-2px; }
        .website-preview-root .cs-month-year { font-size:14px; font-weight:700; color:rgba(255,255,255,0.85); text-transform:uppercase; line-height:1.35; letter-spacing:1px; }
        .website-preview-root .cs-date-right { flex:1; min-width:0; }
        .website-preview-root .cs-badge { display:inline-block; background:rgba(255,255,255,0.15); border:1px solid rgba(255,255,255,0.3); color:#fff; font-size:11px; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; padding:3px 11px; border-radius:6px; margin-bottom:7px; }
        .website-preview-root .cs-program-label { color:rgba(255,255,255,0.9); font-size:14px; font-weight:600; margin:0; line-height:1.4; }
        .website-preview-root .cs-sessions { background:#fff; padding:0; }
        .website-preview-root .cs-table-head { display:grid; grid-template-columns:200px 1fr 220px; align-items:center; gap:16px; padding:11px 32px; background:linear-gradient(90deg,${previewTheme.purpleDark}08,${previewTheme.fuchsia}10); border-bottom:2px solid rgba(107,25,163,0.1); }
        .website-preview-root .cs-th { font-size:10px; font-weight:800; color:${previewTheme.purple}; text-transform:uppercase; letter-spacing:0.14em; display:flex; align-items:center; gap:5px; }
        .website-preview-root .cs-th::before { content:''; display:inline-block; width:3px; height:11px; background:${previewTheme.fuchsia}; border-radius:2px; }
        .website-preview-root .cs-sessions-inner { display:flex; flex-direction:column; }
        .website-preview-root .cs-session-row { display:grid; grid-template-columns:200px 1fr 220px; align-items:center; gap:20px; padding:18px 32px; border-bottom:1px solid rgba(107,25,163,0.06); transition:background 0.18s; }
        .website-preview-root .cs-session-row:nth-child(even) { background:rgba(246,239,250,0.45); }
        .website-preview-root .cs-session-time { display:inline-flex; align-items:center; gap:6px; background:${previewTheme.bgTimer}; border:1.5px solid rgba(192,38,211,0.22); color:${previewTheme.purpleDark}; font-size:12px; font-weight:700; padding:5px 11px; border-radius:8px; white-space:nowrap; width:fit-content; }
        .website-preview-root .cs-session-title { font-size:14px; font-weight:600; color:${previewTheme.purpleDark}; line-height:1.55; }
        .website-preview-root .cs-session-doctor { display:flex; align-items:center; gap:10px; }
        .website-preview-root .cs-doctor-avatar-wrap { width:36px; height:36px; flex-shrink:0; border-radius:10px; overflow:hidden; border:2px solid ${previewTheme.bgTimer}; }
        .website-preview-root .cs-doctor-avatar { width:100%; height:100%; object-fit:cover; }
        .website-preview-root .cs-doctor-info { display:flex; flex-direction:column; gap:1px; min-width:0; }
        .website-preview-root .cs-doctor-role { font-size:9px; font-weight:700; color:${previewTheme.fuchsia}; text-transform:uppercase; letter-spacing:0.1em; }
        .website-preview-root .cs-doctor-name { font-size:13px; font-weight:700; color:${previewTheme.purpleDark}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:160px; }
        .website-preview-root .cs-block-footer { display:flex; justify-content:center; align-items:center; gap:14px; padding:18px 24px 22px; background:${previewTheme.bgLight}; border-top:1px solid rgba(107,25,163,0.08); }
        .website-preview-root .cs-register-btn { display:inline-flex; align-items:center; gap:7px; background:linear-gradient(135deg,${previewTheme.fuchsia},${previewTheme.purple}); color:#fff; font-size:13.5px; font-weight:700; padding:10px 26px; border:none; border-radius:50px; cursor:pointer; box-shadow:0 6px 18px rgba(192,38,211,0.3); white-space:nowrap; }
        .website-preview-root .cs-download-btn { display:inline-flex; align-items:center; gap:7px; background:#fff; color:${previewTheme.purpleDark}; font-size:13.5px; font-weight:700; padding:9px 22px; border:1.5px solid rgba(107,25,163,0.2); border-radius:50px; cursor:pointer; white-space:nowrap; }
        @media(max-width:1024px){
          .website-preview-root .cs-table-head{grid-template-columns:150px 1fr;padding:10px 20px;}
          .website-preview-root .cs-th:last-child{display:none;}
          .website-preview-root .cs-session-row{grid-template-columns:150px 1fr;grid-template-rows:auto auto;gap:6px 14px;padding:14px 20px;}
          .website-preview-root .cs-session-time{grid-row:1;grid-column:1;}
          .website-preview-root .cs-session-title{grid-row:1;grid-column:2;}
          .website-preview-root .cs-session-doctor{grid-row:2;grid-column:2;}
        }
        @media(max-width:767px){
          .website-preview-root .cs-table-head{display:none;}
          .website-preview-root .cs-sessions-inner{padding:16px;gap:12px;background:#faf8fc;}
          .website-preview-root .cs-session-row{display:flex;flex-direction:column;align-items:flex-start;gap:12px;padding:16px;background:#fff !important;border:1px solid rgba(107,25,163,0.15) !important;border-radius:12px;}
          .website-preview-root .cs-block-footer{flex-direction:column;}
        }
      `}</style>
    </App>
  );
};

export default ConferenceForm;